import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import { isMobile } from 'react-device-detect';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import moment, { Moment } from 'moment';
import { TTaskAssigneeOption } from '../../../types/Tasks';
import { TDispatch } from '../../../types/Thunk';
import { useInspection } from '../../../hooks/useInspection';
import { loadStoredRespondentState } from '../../../hooks/useCurrentActiveSurvey';
import {
  createInspectionTask,
  getTaskAssignees,
  setInspectionTaskSent,
} from '../../../actions/InspectionActions';
import ImageUploader from '../../../components/ImageUploader';
import AlertBlock from '../../../components/AlertBlock';
import AssigneeChangeModal from '../../../components/tasks/AssigneeChangeModal';
import SurveyTranslation from '../../../components/survey/SurveyTranslation';

type TPendingFile = {
  filename: string;
  content: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    submitButton: {
      marginTop: theme.spacing(1),
    },
    filesContainer: {
      border: '2px dashed #ccc',
      borderRadius: 8,
      padding: theme.spacing(1),
    },
    fileList: {
      padding: 0,
    },
    fileDivider: {
      borderBottom: '1px dashed #ccc',
    },
    uploadArea: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 2),
    },
    cameraIcon: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      backgroundColor: '#4caf50',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    sizeNote: {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.5),
    },
  })
);

const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const InspectionTaskCreatePage: React.FC<RouteComponentProps> = ({
  history,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const dispatch = useDispatch<TDispatch>();
  const { inspectionQustionId } = useInspection();
  const respondentIdentifier = loadStoredRespondentState();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Moment>(moment());
  const [assigneeEmail, setAssigneeEmail] = useState<string | null>(null);
  const [notifyAssignee, setNotifyAssignee] = useState(true);
  const [notifyAssigneeComment, setNotifyAssigneeComment] = useState('');
  const [assignees, setAssignees] = useState<TTaskAssigneeOption[]>([]);
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [files, setFiles] = useState<TPendingFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getTaskAssignees()).then((result: any) => {
      if (result?.data?.assignees) {
        setAssignees(result.data.assignees);
      }
    });
  }, [dispatch]);

  const assigneeName =
    assignees.find((a) => a.email === assigneeEmail)?.name ?? '';

  const handleAssigneeChange = (
    email: string | null,
    notify: boolean,
    comment: string
  ) => {
    setAssigneeEmail(email);
    setNotifyAssignee(notify);
    setNotifyAssigneeComment(comment);
  };

  const handleFileUpload = (filename: string, content: string) => {
    const base64Part = content.includes(',')
      ? content.split(',')[1]
      : content;
    const padding = (base64Part.match(/=+$/) || [''])[0].length;
    const byteLength = Math.floor(base64Part.length * 3 / 4) - padding;
    if (byteLength > MAX_FILE_SIZE_BYTES) {
      setError(
        t('inspection.task.create.file_too_large', { filename })
      );
      return;
    }
    setFiles((prev) => [...prev, { filename, content }]);
  };

  const handleFileDelete = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await dispatch(
        createInspectionTask({
          title: title.trim(),
          description: description.trim(),
          dueDate: dueDate ? dueDate.format('YYYY-MM-DD HH:mm:ss') : null,
          assigneeEmail,
          notifyAssignee,
          notifyAssigneeComment: notifyAssignee ? notifyAssigneeComment : null,
          respondentId: respondentIdentifier?.id
            ? String(respondentIdentifier.id)
            : null,
          questionId: inspectionQustionId > 0 ? inspectionQustionId : null,
          files,
        })
      );

      dispatch(setInspectionTaskSent(true));
      history.goBack();
    } catch (err: any) {
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage || t('inspection.task.create.error'));
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {error && (
        <AlertBlock type="error" onClose={() => setError(null)}>
          {error}
        </AlertBlock>
      )}

      <Box className={classes.form}>
        <TextField
          label={t('inspection.task.create.title')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          variant="outlined"
          autoFocus
        />

        <TextField
          label={t('inspection.task.create.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          variant="outlined"
        />

        <MobileDateTimePicker
          label={t('inspection.task.create.due_date')}
          ampm={false}
          format="YYYY-MM-DD HH:mm:ss"
          value={dueDate}
          onChange={(date) => {
            if (date) {
              setDueDate(date);
            }
          }}
          slotProps={{
            textField: { variant: 'outlined', fullWidth: true },
            actionBar: { actions: ['today', 'accept'] },
          }}
        />

        <TextField
          label={t('tasks.detail.assignee')}
          variant="outlined"
          fullWidth
          value={assigneeName}
          onClick={() => setAssigneeModalOpen(true)}
          InputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
        />

        <AssigneeChangeModal
          open={assigneeModalOpen}
          onClose={() => setAssigneeModalOpen(false)}
          onApply={handleAssigneeChange}
          assignees={assignees}
          currentAssigneeEmail={assigneeEmail}
        />

        <Typography variant="subtitle2">
          {t('inspection.task.create.files')}
        </Typography>
        <Box className={classes.filesContainer}>
          {files.length > 0 && (
            <>
              <List className={classes.fileList}>
                {files.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.filename}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleFileDelete(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <div className={classes.fileDivider} />
            </>
          )}

          <ImageUploader
            handlePhotoUpload={handleFileUpload}
            accept="*/*"
            wrapperStyle={{ display: 'inline-block' }}
          >
            <Box className={classes.uploadArea}>
              {isMobile ? (
                <div className={classes.cameraIcon}>
                  <CameraAltIcon
                    style={{ color: '#fff', fontSize: '1.25rem' }}
                  />
                </div>
              ) : (
                <Button variant="outlined" size="small" component="span">
                  {t('tasks.detail.upload_files')}
                </Button>
              )}
            </Box>
          </ImageUploader>
          <Typography className={classes.sizeNote}>
            {t('tasks.detail.file_size_limit')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={handleSubmit}
          disabled={submitting || !title.trim()}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <SurveyTranslation translate="inspection.task.create.submit" />
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default withRouter(InspectionTaskCreatePage);
